'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { HiOutlineShoppingCart } from '@react-icons/all-files/hi/HiOutlineShoppingCart';
import { HiOutlinePencilAlt } from '@react-icons/all-files/hi/HiOutlinePencilAlt';
import { HiOutlineColorSwatch } from '@react-icons/all-files/hi/HiOutlineColorSwatch';
import { HiOutlineDesktopComputer } from '@react-icons/all-files/hi/HiOutlineDesktopComputer';
import { HiOutlineHome } from '@react-icons/all-files/hi/HiOutlineHome';
import { HiOutlineSparkles } from '@react-icons/all-files/hi/HiOutlineSparkles';
import { HiOutlineTerminal } from '@react-icons/all-files/hi/HiOutlineTerminal';
import { IUseCase, FILTER_TABS } from './useCaseData';
import {
  StyledHero,
  StyledStatBar,
  StyledGrid,
  StyledGridInner,
  StyledFilterBar,
  StyledFilterPill,
  StyledCardsGrid,
  StyledUseCaseCard,
  StyledWidgetChips,
  StyledCardCta,
  StyledBottomCta,
  StyledCtaButtons,
} from './UseCasesHub.styles';

const USE_CASE_ICONS: Record<string, React.ReactNode> = {
  ecommerce: <HiOutlineShoppingCart />,
  'content-creators': <HiOutlinePencilAlt />,
  agencies: <HiOutlineColorSwatch />,
  saas: <HiOutlineDesktopComputer />,
  'real-estate': <HiOutlineHome />,
  events: <HiOutlineSparkles />,
  developers: <HiOutlineTerminal />,
};

const CTA_LABELS: Record<string, string> = {
  ecommerce: 'Explore E-Commerce Widgets',
  'content-creators': 'Explore Creator Widgets',
  agencies: 'Explore Agency Widgets',
  saas: 'Explore SaaS Widgets',
  'real-estate': 'Explore Local Widgets',
  events: 'Explore Event Widgets',
  developers: 'Explore Dev Platform',
};

interface IProps {
  useCases: IUseCase[];
}

export const UseCasesHub = ({ useCases }: IProps) => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filtered =
    activeFilter === 'all'
      ? useCases
      : useCases.filter((uc) => uc.filter === activeFilter);

  return (
    <>
      <StyledHero>
        <h1>Website Widgets for Every Use Case</h1>
        <p>
          Whether you run an online store, a blog, a real estate site, or a SaaS
          product, Common Ninja has the exact widgets you need. No coding, no
          hassle. Just pick, customize, and embed.
        </p>
        <StyledStatBar>
          <span>500K+ Businesses</span>
          <span>200+ Widgets</span>
          <span>200+ Platforms</span>
          <span>No Coding Required</span>
        </StyledStatBar>
      </StyledHero>

      <StyledGrid>
        <StyledGridInner>
          <StyledFilterBar>
            {FILTER_TABS.map((tab) => (
              <StyledFilterPill
                key={tab.key}
                $active={activeFilter === tab.key}
                onClick={() => setActiveFilter(tab.key)}
              >
                {tab.label}
              </StyledFilterPill>
            ))}
          </StyledFilterBar>

          <StyledCardsGrid>
            {filtered.map((uc) => (
              <StyledUseCaseCard key={uc.slug}>
                <span className="card-icon">
                  {USE_CASE_ICONS[uc.slug]}
                </span>
                <h2>{uc.name}</h2>
                <p>{uc.tagline}</p>
                <StyledWidgetChips>
                  {uc.widgetSlugs.slice(0, 4).map((slug, i) => (
                    <Link key={slug} href={`/widgets/${slug}`}>
                      {uc.widgetLabels[i]}
                    </Link>
                  ))}
                </StyledWidgetChips>
                <StyledCardCta as={Link} href={`/use-cases/${uc.slug}`}>
                  {CTA_LABELS[uc.slug] || `Explore Widgets`}
                </StyledCardCta>
              </StyledUseCaseCard>
            ))}
          </StyledCardsGrid>
        </StyledGridInner>
      </StyledGrid>

      <StyledBottomCta>
        <h2>Can&apos;t find your use case?</h2>
        <p>
          With 200+ widgets across 13 categories, Common Ninja has something for
          every website. Browse the full widget library or use our AI widget
          builder to create exactly what you need.
        </p>
        <StyledCtaButtons>
          <Link href="/widgets" className="primary">
            Browse All Widgets
          </Link>
          <Link href="/features/ai" className="secondary">
            Build a Custom Widget with AI
          </Link>
        </StyledCtaButtons>
      </StyledBottomCta>
    </>
  );
};
